import { useEffect, useState } from 'react';

/**
 * A sentence that TYPES ITSELF OUT, a character at a time (SPEC-004 D-62).
 *
 * The fourth timed hook in this folder and built to the same rules as the other
 * three: the sequence is the only interesting part, every browser API is a
 * defaulted parameter hoisted to module scope, and the whole thing is walkable
 * by a test without a clock. `useCountUp` is the nearest sibling — that one
 * rolls a number to its new value, this one spells a sentence out — and the two
 * are deliberately the same shape.
 *
 * WHY THE TAGLINE AND NOTHING ELSE. It is the one line on this screen that is
 * neither a measurement nor a name: somebody wrote it, about this car, to be
 * read. The figures around it change by rolling because they are quantities and
 * a quantity can travel; a sentence cannot, so it arrives the way a sentence
 * does — one character after another, at reading speed.
 *
 * IT RE-TYPES WHEN THE SENTENCE CHANGES, and only then. The text is the
 * dependency, so a re-render that happens to land mid-swipe — and this screen
 * re-renders on every frame of one — does not start the sentence again.
 *
 * THE CARET IS NOT HERE. Whether something blinks at the end of the line is a
 * question for the stylesheet, and `isTyping` is what it needs to answer it.
 */

const defaultSchedule = (callback, delay) => (
    typeof setTimeout === 'undefined' ? null : setTimeout(callback, delay)
);

const defaultCancel = (handle) => {
    if (typeof clearTimeout !== 'undefined' && handle !== null) {
        clearTimeout(handle);
    }
};

const defaultPrefersReducedMotion = () => typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * How long each character takes.
 *
 * Eighteen milliseconds is about fifty-five characters a second — faster than
 * anybody types and slower than the eye can take in at once, which is the band
 * this effect has to sit in. The taglines in the pack run to about forty
 * characters, so a sentence lands in well under a second; slower than that and
 * the reader is waiting for a line they can already half-read.
 */
export const CHARACTER_MILLISECONDS = 18;

export default function useTypewriter(text, {
    characterMs = CHARACTER_MILLISECONDS,
    schedule = defaultSchedule,
    cancel = defaultCancel,
    prefersReducedMotion = defaultPrefersReducedMotion,
} = {}) {
    const full = typeof text === 'string' ? text : '';
    const [shown, setShown] = useState(full);

    useEffect(() => {
        // A reader who asked for less movement gets the sentence, not a faster
        // performance of it. There is no reading order being preserved here and
        // nothing to understand from the typing — it is ornament, and ornament
        // is exactly what the preference is about.
        if (!full || prefersReducedMotion()) {
            setShown(full);

            return undefined;
        }

        let count = 0;
        let handle = null;

        setShown('');

        const next = () => {
            count += 1;
            // Sliced from the WHOLE string every time rather than appended to,
            // so the sentence on screen is always a real prefix of the sentence
            // in the pack. Appending would let a cancelled run leave a hybrid of
            // two cars' taglines on the card.
            setShown(full.slice(0, count));

            handle = count < full.length ? schedule(next, characterMs) : null;
        };

        handle = schedule(next, characterMs);

        // A platform with no timer at all — a server render — gets the finished
        // sentence rather than an empty line.
        if (handle === null) {
            setShown(full);
        }

        return () => cancel(handle);
    }, [full, characterMs]);

    return { shown, isTyping: shown.length < full.length };
}
